import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@fontsource/mukta/400.css';
import '@fontsource/mukta/500.css';
import '@fontsource/mukta/600.css';
import '@fontsource/mukta/700.css';
import '@fontsource/mukta/800.css';
import '@fontsource/barlow-semi-condensed/600.css';
import '@fontsource/barlow-semi-condensed/700.css';
import './styles.css';
import { ThemeProvider } from './theme';
import { SessionProvider, homeFor, useSession } from './session';
import { Layout, MorePage } from './Layout';
import { Loading } from './ui';
import { Login } from './pages/Login';
import { About, Privacy } from './pages/Public';
import { Landing } from './pages/Landing';
import { Welcome } from './pages/Welcome';
import { Choose, Invite, Account, Notifications, DevInbox } from './pages/Common';
import { BuyerDashboard, Approvals } from './pages/buyer/Dashboard';
import { IndentList, NewIndent, IndentDetail } from './pages/buyer/Indents';
import { People } from './pages/buyer/People';
import { Policy } from './pages/buyer/Policy';
import { BuyerSuppliers, BuyerSupplierDetail, BuyerPayments } from './pages/buyer/Suppliers';
import { DriverNotice } from './pages/driver/Driver';
import { QrShare } from './pages/QrShare';
import { Station } from './pages/station/Station';
import { SupplierDashboard, Claims } from './pages/supplier/Dashboard';
import { Customers, CustomerDetail } from './pages/supplier/Customers';
import { SupplierPayments, PaymentDetail } from './pages/supplier/Payments';
import { Setup } from './pages/supplier/Setup';
import { BusinessProfile } from './pages/shared/BusinessProfile';
import { Accounting } from './pages/supplier/Accounting';
import { StatementView } from './pages/shared/Statement';
import { Disputes, DisputeDetail } from './pages/shared/Disputes';
import { Adjustments } from './pages/shared/Adjustments';
import { Reports } from './pages/shared/Reports';
import { Audit } from './pages/shared/Audit';
import { Platform } from './pages/platform/Platform';
import { SupplyDetail } from './pages/shared/Supply';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: (n, e) => n < 2 && (e as { status?: number }).status !== 403 && (e as { status?: number }).status !== 404, refetchOnWindowFocus: true } },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { me, loading } = useSession();
  const loc = useLocation();
  if (loading) return <Loading />;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (me.station && !me.station.actingUserId && loc.pathname !== '/station') return <Navigate to="/station" replace />;
  return <>{children}</>;
}

function AccountHome() {
  const { me, active } = useSession();
  if (!me) return <Navigate to="/login" replace />;
  if (me.pendingInvitations.length) return <Navigate to="/choose" replace />;
  // No business yet: confirm the mobile (which brings in any access already
  // given to that number) or register a fleet or pump.
  if (!me.memberships.length) return <Navigate to="/welcome" replace />;
  return <Navigate to={homeFor(active)} replace />;
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<About />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/login" element={<Login />} />
      <Route path="/invite/:token" element={<Invite />} />
      <Route path="/dev/inbox" element={<DevInbox />} />
      <Route path="/qr" element={<QrShare />} />
      <Route element={<RequireAuth><Layout /></RequireAuth>}>
        <Route path="/app" element={<AccountHome />} />
        <Route path="/choose" element={<Choose />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/account" element={<Account />} />
        <Route path="/more" element={<MorePage />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/buyer" element={<BuyerDashboard />} />
        <Route path="/buyer/indents" element={<IndentList />} />
        <Route path="/buyer/indents/new" element={<NewIndent />} />
        <Route path="/buyer/indents/:id" element={<IndentDetail />} />
        <Route path="/buyer/approvals" element={<Approvals />} />
        <Route path="/buyer/people" element={<People />} />
        <Route path="/buyer/policy" element={<Policy />} />
        <Route path="/buyer/suppliers" element={<BuyerSuppliers />} />
        <Route path="/buyer/suppliers/:id" element={<BuyerSupplierDetail />} />
        <Route path="/buyer/payments" element={<BuyerPayments />} />
        <Route path="/driver/*" element={<DriverNotice />} />
        <Route path="/station" element={<Station />} />
        <Route path="/supplier" element={<SupplierDashboard />} />
        <Route path="/supplier/customers" element={<Customers />} />
        <Route path="/supplier/customers/:id" element={<CustomerDetail />} />
        <Route path="/supplier/payments" element={<SupplierPayments />} />
        <Route path="/supplier/payments/:id" element={<PaymentDetail />} />
        <Route path="/supplier/claims" element={<Claims />} />
        <Route path="/supplier/setup" element={<Setup />} />
        <Route path="/org/profile" element={<BusinessProfile />} />
        <Route path="/supplier/accounting" element={<Accounting />} />
        <Route path="/statements/:id" element={<StatementView />} />
        <Route path="/supplies/:id" element={<SupplyDetail />} />
        <Route path="/disputes" element={<Disputes />} />
        <Route path="/disputes/:id" element={<DisputeDetail />} />
        <Route path="/adjustments" element={<Adjustments />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/audit" element={<Audit />} />
        <Route path="/platform" element={<Platform />} />
        <Route path="*" element={<div className="empty">Page not found.</div>} />
      </Route>
    </Routes>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <SessionProvider>
            <App />
          </SessionProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
